import React, {
  createContext,
  useState,
  useContext,
  useMemo,
  useEffect,
} from "react";
import { MINI_STORE_PAGINATION_LIMIT } from "../../utilities/constants";
import { cartUpdate } from "../../DataApi/miniStore/cartUpdate";
import { getToken } from "../../DataApi/miniStore/getToken";
import { shopCart } from "../../DataApi/miniStore/shopCart";
import { cartUpdateJson } from "../../DataApi/miniStore/cartUpdateJson";
export const CartContext = createContext();

const CART_STORAGE_KEY = "cartData";
const CART_EXPIRY_KEY = "cartExpiry";

// Default cart expiry (3 days)
const DEFAULT_EXPIRY_TIME = { value: 7, unit: "days" };

// Function to calculate expiration timestamp
const getExpiryTimestamp = ({ value, unit }) => {
  const now = new Date().getTime();
  switch (unit) {
    case "minutes":
      return now + value * 60 * 1000;
    case "hours":
      return now + value * 60 * 60 * 1000;
    case "days":
    default:
      return now + value * 24 * 60 * 60 * 1000;
  }
};

// Function to check if cart is expired
const isCartExpired = () => {
  const expiry = localStorage.getItem(CART_EXPIRY_KEY);
  return expiry && new Date().getTime() > parseInt(expiry, 10);
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [subTotal, setSubTotal] = useState("");
  const [total, setTotal] = useState("");
  const [currency, setCurrency] = useState("");
  const [cartCount, setCartCount] = useState(0);
  const [cartTax, setCartTax] = useState(0);
  const [shippingCost, setShippingCost] = useState(0);
  const [cartLoading, setCartLoading] = useState(true);
  const [promoCode, setPromoCode] = useState("");
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [message, setMessage] = useState(null);
  const [products, setProducts] = useState([]);
  const [variants, setVariants] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [totalPages, setTotalPages] = useState(1);

  const [categories, setCategories] = useState([]);
  const [searchBy, setSearchBy] = useState("category");
  const [cartExpiry, setCartExpiry] = useState(DEFAULT_EXPIRY_TIME);

  // Load cart from localStorage on mount
  useEffect(() => {
    if (!isCartExpired()) {
      const storedCart = localStorage.getItem(CART_STORAGE_KEY);
      if (storedCart) {
        setCart(JSON.parse(storedCart));
      }

      const currency = localStorage.getItem("currency");
      const subTotal = localStorage.getItem("subTotal");
      const total = localStorage.getItem("total");
      const cartCount = localStorage.getItem("cartCount");
      const shippingCost = localStorage.getItem("shippingCost");
      const cartTax = localStorage.getItem("cartTax");

      if (currency) setCurrency(currency);
      if (subTotal) setSubTotal(subTotal);
      if (total) setTotal(total);
      if (cartCount) setCartCount(parseInt(cartCount));
      if (shippingCost) setShippingCost(parseFloat(shippingCost));
      if (cartTax) setCartTax(parseFloat(cartTax));
    } else {
      localStorage.removeItem(CART_STORAGE_KEY);
      localStorage.removeItem(CART_EXPIRY_KEY);
      localStorage.removeItem("currency");
      localStorage.removeItem("subTotal");
      localStorage.removeItem("total");
      localStorage.removeItem("cartCount");
      localStorage.removeItem("shippingCost");
      localStorage.removeItem("cartTax");
    }
    setCartLoading(false);
  }, []);

  // Save cart to localStorage whenever it updates
  useEffect(() => {
    if (cart.length > 0) {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
      localStorage.setItem(
        CART_EXPIRY_KEY,
        getExpiryTimestamp(cartExpiry).toString()
      );
      localStorage.setItem("currency", currency);
      localStorage.setItem("subTotal", subTotal);
      localStorage.setItem("total", total);
      localStorage.setItem("cartCount", cartCount.toString());
      localStorage.setItem("shippingCost", shippingCost.toString());
      localStorage.setItem("cartTax", cartTax.toString());
    }
  }, [
    cart,
    cartExpiry,
    currency,
    subTotal,
    total,
    cartCount,
    shippingCost,
    cartTax,
  ]);

  const setPageCount = (count) => {
    const pageCount = Math.ceil(count / MINI_STORE_PAGINATION_LIMIT) || 1;
    setTotalPages(pageCount);
    // console.log("totalPages", totalPages);
  };
  const applyPromoCode = (promoCode) => {
    // Check if promo code exists
    // If the same promo is already applied, show info message and return
    // if (foundPromo && promoCode === lastAppliedPromo) {
    //   setMessage({
    //     type: "info",
    //     text: "⚠️ This promo code is already applied!",
    //   });
    //   return;
    // }
    // if (foundPromo) {
    //   setPromoDiscount((foundPromo.discount * 1) / 100);
    //   setLastAppliedPromo(promoCode); // Update last applied promo
    //   setMessage({
    //     type: "success",
    //     text: `✅ Promo Code applied successfully! Enjoy ${foundPromo.discount}% off`,
    //   });
    // } else {
    //   setPromoDiscount(0);
    //   setLastAppliedPromo(""); // Reset last applied promo when invalid
    //   setMessage({ type: "error", text: "❌ Invalid Promo Code" });
    // }
  };

  const totalDiscount = useMemo(
    () =>
      cart.reduce(
        (sum, item) =>
          sum +
          ((parseFloat(item.price) * (item.discount || 0)) / 100) *
            item.quantity,
        0
      ),
    [cart]
  );

  // Add product to cart
  const addToCart = async ({ productId: variantId }) => {
    try {
      const tokenData = await getToken();
      if (!tokenData?.token) return;
      const token = tokenData.token;

      const orderId =
        cart.find((item) => item.order_id !== undefined)?.order_id || false;

      const vals = {
        products: [
          {
            id: variantId, // send variantId in the API call
            quantity: 1, // always adding 1
          },
        ],
      };
      // console.log("variant id in cart context addToCart", variantId);
      const shopCartPayload = {
        csrf_token: token,
        product_id: variantId,
        quantity: 1,
        product_custom_attribute_values: [],
        variant_values: null,
        no_variant_attribute_values: [],
        add_qty: 1,
      };
      const responseShopCart = await shopCart(shopCartPayload);
      // console.log("responseShopCart", responseShopCart);
      const response = await cartUpdate({ token, order_id: orderId, vals });
      if (
        response?.status === 200 &&
        Array.isArray(response.result.order_line)
      ) {
        // Directly build synced cart from API result
        const syncedCart = response.result.order_line.map((apiItem) => ({
          id: apiItem.id,
          line_id: apiItem.line_id,
          template_id: apiItem.template_id || null,
          title: apiItem.name,
          description: apiItem.description_sale,
          rating: apiItem.rating,
          quantity: apiItem.quantity,
          currency: apiItem.currency,
          price: apiItem.price,
          newPrice: apiItem.newPrice,
          has_discounted_price: apiItem.has_discounted_price,
          totalPrice: apiItem.totalPrice,
          discount: apiItem.discount,
          image_1920: apiItem.image,
          hasVariant: apiItem.hasVariant,
          order_id: response.order_id,
        }));

        let {
          amount_untaxed,
          amount_total,
          currency_name,
          amount_tax,
          amount_delivery,
          cart_quantity,
        } = response.result;
        currency_name = currency_name || "";
        setCart(syncedCart);
        setCurrency(currency_name || "");
        setSubTotal(`${currency_name || ""} ${amount_untaxed.toFixed(2)}`);
        setTotal(`${currency_name || ""} ${amount_total.toFixed(2)}`);
        setCartCount(cart_quantity);
        setShippingCost(amount_delivery);
        setCartTax(amount_tax);
        // Save everything to localStorage
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(syncedCart));
        localStorage.setItem(
          CART_EXPIRY_KEY,
          getExpiryTimestamp(cartExpiry).toString()
        );
        localStorage.setItem("currency", currency_name || "");
        localStorage.setItem(
          "subTotal",
          `${currency_name || ""} ${amount_untaxed.toFixed(2)}`
        );
        localStorage.setItem(
          "total",
          `${currency_name || ""} ${amount_total.toFixed(2)}`
        );
        localStorage.setItem("cartCount", cart_quantity.toString());
        localStorage.setItem("shippingCost", amount_delivery.toString());
        localStorage.setItem("cartTax", amount_tax.toString());
      }
      // const responseShopCart = await shopCart(shopCartPayload);
      // console.log("responseShopCart", responseShopCart);
      return true;
    } catch (err) {
      console.error("Add to cart error:", err);
      return false;
    }
  };
  const updateQuantity = async ({ productId: variantId, qtyChange }) => {
    // console.log("in updateQuantity", variantId);
    try {
      const tokenData = await getToken();
      if (!tokenData?.token) return;
      const token = tokenData.token;

      const orderId =
        cart.find((item) => item.order_id !== undefined)?.order_id || false;
      // Find the existing cart item and calculate the new quantity
      const cartItem = cart.find((item) => item.id === variantId);
      if (!cartItem) return;

      const line_id = cartItem?.line_id || false;
      // const updatedQuantity = Math.max(1, (cartItem.quantity || 1) + qtyChange);
      const updatedQuantity = Math.max(0, (cartItem.quantity || 0) + qtyChange);
      const updateJsonPayload = {
        line_id: line_id,
        product_id: variantId,
        set_qty: updatedQuantity,
      };
      const responseCartUpdateJson = await cartUpdateJson(updateJsonPayload);
      // console.log("responseCartUpdateJson", responseCartUpdateJson);
      const vals = {};

      const response = await cartUpdate({ token, order_id: orderId, vals });

      if (
        response?.status === 200 &&
        Array.isArray(response.result.order_line)
      ) {
        // Rebuild cart from API response
        const syncedCart = response.result.order_line.map((apiItem) => ({
          id: apiItem.id,
          line_id: apiItem.line_id,
          template_id: apiItem.template_id || null,
          title: apiItem.name,
          description: apiItem.description_sale,
          rating: apiItem.rating,
          quantity: apiItem.quantity,
          currency: apiItem.currency,
          price: apiItem.price,
          newPrice: apiItem.newPrice,
          has_discounted_price: apiItem.has_discounted_price,
          totalPrice: apiItem.totalPrice,
          discount: apiItem.discount,
          image_1920: apiItem.image,
          hasVariant: apiItem.hasVariant,
          order_id: response.order_id,
        }));

        let {
          amount_untaxed,
          amount_total,
          currency_name,
          amount_tax,
          amount_delivery,
          cart_quantity,
        } = response.result;
        currency_name = currency_name || "";
        setCart(syncedCart);
        setCurrency(currency_name);
        setSubTotal(`${currency_name} ${amount_untaxed.toFixed(2)}`);
        setTotal(`${currency_name} ${amount_total.toFixed(2)}`);
        setCartCount(cart_quantity);
        setShippingCost(amount_delivery);
        setCartTax(amount_tax);
        // Save everything to localStorage
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(syncedCart));
        localStorage.setItem(
          CART_EXPIRY_KEY,
          getExpiryTimestamp(cartExpiry).toString()
        );
        localStorage.setItem("currency", currency_name);
        localStorage.setItem(
          "subTotal",
          `${currency_name} ${amount_untaxed.toFixed(2)}`
        );
        localStorage.setItem(
          "total",
          `${currency_name} ${amount_total.toFixed(2)}`
        );
        localStorage.setItem("cartCount", cart_quantity.toString());
        localStorage.setItem("shippingCost", amount_delivery.toString());
        localStorage.setItem("cartTax", amount_tax.toString());
      }
      return true;
    } catch (err) {
      console.error("Update quantity error:", err);
      return false;
    }
  };

  //resetCart
  const handleResetCart = () => {
    localStorage.removeItem(CART_STORAGE_KEY);
    localStorage.removeItem(CART_EXPIRY_KEY);
    localStorage.removeItem("currency");
    localStorage.removeItem("subTotal");
    localStorage.removeItem("total");
    localStorage.removeItem("cartCount");
    localStorage.removeItem("shippingCost");
    localStorage.removeItem("cartTax");
  };

  // Remove item from cart
  const removeFromCart = async ({ productId }) => {
    try {
      const tokenData = await getToken();
      if (!tokenData?.token) return;
      const token = tokenData.token;

      const orderId =
        cart.find((item) => item.order_id !== undefined)?.order_id || false;
      // Find the existing cart item and calculate the new quantity
      const cartItem = cart.find((item) => item.id === productId);
      if (!cartItem) return;

      const line_id = cartItem?.line_id || false;
      const updateJsonPayload = {
        line_id: line_id,
        product_id: productId,
        set_qty: 0,
      };
      const responseCartUpdateJson = await cartUpdateJson(updateJsonPayload);
      // console.log("responseCartUpdateJson", responseCartUpdateJson);
      const vals = {};

      const response = await cartUpdate({ token, order_id: orderId, vals });

      if (
        response?.status === 200 &&
        Array.isArray(response.result.order_line)
      ) {
        // Rebuild cart from API response
        const syncedCart = response.result.order_line.map((apiItem) => ({
          id: apiItem.id,
          line_id: apiItem.line_id,
          template_id: apiItem.template_id || null,
          title: apiItem.name,
          description: apiItem.description_sale,
          rating: apiItem.rating,
          quantity: apiItem.quantity,
          currency: apiItem.currency,
          price: apiItem.price,
          newPrice: apiItem.newPrice,
          has_discounted_price: apiItem.has_discounted_price,
          totalPrice: apiItem.totalPrice,
          discount: apiItem.discount,
          image_1920: apiItem.image,
          hasVariant: apiItem.hasVariant,
          order_id: response.order_id,
        }));

        let {
          amount_untaxed,
          amount_total,
          currency_name,
          amount_tax,
          amount_delivery,
          cart_quantity,
        } = response.result;
        currency_name = currency_name || "";
        setCart(syncedCart);
        setCurrency(currency_name);
        setSubTotal(`${currency_name} ${amount_untaxed.toFixed(2)}`);
        setTotal(`${currency_name} ${amount_total.toFixed(2)}`);
        setCartCount(cart_quantity);
        setShippingCost(amount_delivery);
        setCartTax(amount_tax);
        // Save everything to localStorage
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(syncedCart));
        localStorage.setItem(
          CART_EXPIRY_KEY,
          getExpiryTimestamp(cartExpiry).toString()
        );
        localStorage.setItem("currency", currency_name);
        localStorage.setItem(
          "subTotal",
          `${currency_name} ${amount_untaxed.toFixed(2)}`
        );
        localStorage.setItem(
          "total",
          `${currency_name} ${amount_total.toFixed(2)}`
        );
        localStorage.setItem("cartCount", cart_quantity.toString());
        localStorage.setItem("shippingCost", amount_delivery.toString());
        localStorage.setItem("cartTax", amount_tax.toString());
      }
    } catch (err) {
      console.error("Update quantity error:", err);
    }
  };
  const emptyCart = (productId) => {
    const updatedCart = cart.filter((item) => item.id !== productId);

    // Update state
    setCart(updatedCart);

    // Update local storage
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(updatedCart));
    localStorage.setItem(
      CART_EXPIRY_KEY,
      getExpiryTimestamp(cartExpiry).toString()
    );
  };

  const setCartExpiryTime = (value, unit) => {
    setCartExpiry({ value, unit });
  };
  // Get total number of unique items in the cart
  // const cartCount = useMemo(
  //   () => cart.reduce((acc, item) => acc + item.quantity, 0),
  //   [cart]
  // );

  return (
    <CartContext.Provider
      value={{
        cartLoading,
        handleResetCart,
        setCartLoading,
        addToCart,
        updateQuantity,
        removeFromCart,
        emptyCart,
        products,
        setProducts,
        variants,
        setVariants,
        selectedVariant,
        setSelectedVariant,
        selectedAttributes,
        setSelectedAttributes,
        categories,
        setCategories,
        cart,
        setCart,
        totalDiscount,
        promoDiscount,
        applyPromoCode,
        promoCode,
        setPromoCode,
        message,
        subTotal,
        setSubTotal,
        total,
        setTotal,
        currency,
        setCurrency,
        cartCount,
        setCartCount,
        shippingCost,
        setShippingCost,
        cartTax,
        setCartTax,
        setPageCount,
        totalPages,
        searchBy,
        setSearchBy,
        cartExpiry,
        CART_STORAGE_KEY,
        CART_EXPIRY_KEY,
        getExpiryTimestamp,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default CartProvider;
